import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import {
  aplicarMeuVoto,
  BUSCA_MINIMA_DE_PESSOAS,
  DIGITANDO_EXPIRA_MS,
  DIGITANDO_REENVIO_MS,
  type AcervoDaConversa,
  type CanalDeChat,
  type ConviteDeContato,
  type DuracaoDoSilencio,
  type FotoDoCanal,
  type ResultadoDeBusca,
  type GifDoGiphy,
  type MembroDoCanal,
  type MencaoRecebida,
  type MensagemDoCanal,
  type PessoaDoOrbind,
  type StatusEscolhido,
} from '@orbind/core'
import { api, apiOrigin, webTokenStorage } from '../../lib/api.ts'
import { canalDePresenca } from '../presence/presence-client.ts'

/**
 * Dados do chat.
 *
 * A leitura é toda por React Query, e o canal ao vivo só INVALIDA — nunca
 * escreve mensagem direto no cache. O servidor monta a mensagem com autor,
 * anexos, reações e citação; remontar isso no cliente criaria uma segunda
 * versão da mesma regra, que divergiria na primeira mudança de formato.
 */

export const CHAVE_CANAIS = ['chat', 'channels'] as const
export const chaveMensagens = (channelId: string) => ['chat', 'messages', channelId] as const
export const chaveCanal = (channelId: string) => ['chat', 'channel', channelId] as const

/** `arquivadas: true` busca só as arquivadas (chip "Arquivadas", 2026-09-14). */
export function useCanais(opcoes: { arquivadas?: boolean; ligado?: boolean } = {}) {
  const arquivadas = opcoes.arquivadas ?? false
  return useQuery({
    queryKey: arquivadas ? [...CHAVE_CANAIS, 'arquivadas'] : CHAVE_CANAIS,
    enabled: opcoes.ligado ?? true,
    queryFn: async (): Promise<CanalDeChat[]> =>
      (await api.chat.channels.$get({ query: arquivadas ? { arquivadas: '1' } : {} })).json().then((r) => r.channels),
  })
}

export function useCanal(channelId: string | null) {
  return useQuery({
    queryKey: chaveCanal(channelId ?? ''),
    enabled: channelId !== null,
    queryFn: async (): Promise<{ channel: CanalDeChat; members: MembroDoCanal[] }> => {
      // O RPC devolve `kind` e `papel` como string livre; o catalogo esta em
      // @orbind/core e o servidor ja validou na escrita.
      const dados = await (await api.chat.channels[':id'].$get({ param: { id: channelId! } })).json()
      return dados as unknown as { channel: CanalDeChat; members: MembroDoCanal[] }
    },
  })
}

export function useMensagens(channelId: string | null) {
  return useQuery({
    queryKey: chaveMensagens(channelId ?? ''),
    enabled: channelId !== null,
    queryFn: async (): Promise<MensagemDoCanal[]> =>
      (await api.chat.channels[':id'].messages.$get({ param: { id: channelId! } }))
        .json()
        .then((r) => r.messages),
  })
}

/**
 * Mantém a conversa aberta em dia pelo canal ao vivo.
 *
 * Invalidar (em vez de inserir) custa uma consulta a mais por mensagem, e em
 * troca o que a tela mostra é sempre o que o servidor calculou.
 */
export function useChatAoVivo(channelIdAberto: string | null) {
  const queryClient = useQueryClient()
  useEffect(() => {
    return canalDePresenca.inscrever((evento) => {
      if (evento.tipo !== 'chat') return
      // A lista de conversas muda sempre: prévia e não lidas.
      void queryClient.invalidateQueries({ queryKey: CHAVE_CANAIS })
      if (evento.channelId === channelIdAberto) {
        void queryClient.invalidateQueries({ queryKey: chaveMensagens(evento.channelId) })
        // Foto, nome e membros do grupo também chegam por este aviso.
        void queryClient.invalidateQueries({ queryKey: chaveCanal(evento.channelId) })
      }
    })
  }, [queryClient, channelIdAberto])
}

/** Tudo que muda uma conversa, com a invalidação certa em cada caso. */
export function useAcoesDoChat(channelId: string | null) {
  const queryClient = useQueryClient()
  const invalidar = () => {
    void queryClient.invalidateQueries({ queryKey: CHAVE_CANAIS })
    if (channelId) {
      void queryClient.invalidateQueries({ queryKey: chaveMensagens(channelId) })
      void queryClient.invalidateQueries({ queryKey: chaveCanal(channelId) })
    }
  }

  const enviar = useMutation({
    mutationFn: async (entrada: {
      body?: string
      replyToMessageId?: string | null
      arquivo?: File
      tipoAnexo?: string
      mencoes?: string[]
    }) => {
      const resposta = await api.chat.channels[':id'].messages.$post({
        param: { id: channelId! },
        json: {
          body: entrada.body ?? '',
          replyToMessageId: entrada.replyToMessageId ?? null,
          comArquivo: entrada.arquivo !== undefined,
          mencoes: entrada.mencoes,
        },
      })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível enviar a mensagem.'))
      const { messageId } = await resposta.json()

      // O arquivo sobe DEPOIS da mensagem, para nunca ficar órfão. Se o upload
      // falhar, a mensagem vazia é apagada — senão sobraria um balão sem nada.
      if (entrada.arquivo) {
        const form = new FormData()
        form.append('file', entrada.arquivo)
        form.append('kind', entrada.tipoAnexo ?? 'documento')
        const envio = await fetch(`${apiOrigin()}/api/chat/messages/${messageId}/attachments`, {
          method: 'POST',
          headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
          body: form,
        })
        if (!envio.ok) {
          await api.chat.messages[':messageId'].$delete({ param: { messageId } })
          throw new Error(await mensagemDeErro(envio, 'Não foi possível enviar o arquivo.'))
        }
      }
      return messageId
    },
    onSuccess: invalidar,
  })

  const anexarEstruturado = useMutation({
    mutationFn: async (entrada: { kind: 'contato' | 'enquete' | 'evento' | 'gif'; payload: Record<string, unknown>; body?: string }) => {
      // Uma chamada só: mensagem e anexo nascem juntos no servidor.
      const resposta = await api.chat.channels[':id'].messages.$post({
        param: { id: channelId! },
        json: { body: entrada.body ?? '', estruturado: { kind: entrada.kind, payload: entrada.payload } },
      })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível enviar.'))
      return (await resposta.json()).messageId
    },
    onSuccess: invalidar,
  })

  const reagir = useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      (await api.chat.messages[':messageId'].reactions.$post({ param: { messageId }, json: { emoji } })).json(),
    onSuccess: invalidar,
  })

  const silenciar = useMutation({
    mutationFn: async (duracao: DuracaoDoSilencio | null) => {
      const resposta = await api.chat.channels[':id'].mute.$put({ param: { id: channelId! }, json: { duracao } })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível alterar as notificações.'))
      return resposta.json()
    },
    onSuccess: invalidar,
  })

  /**
   * Votar é OTIMISTA: a marca e a barra mudam no clique, antes do servidor
   * responder. Esperar a volta da conversa inteira deixava 3–4 s de atraso
   * (pedido 2026-09-14). Falhou? A enquete volta ao que era e a tela avisa.
   */
  const votar = useMutation({
    mutationFn: async ({ messageId, opcao }: { messageId: string; opcao: number; meuNome: string }) => {
      const resposta = await api.chat.messages[':messageId'].poll.vote.$post({ param: { messageId }, json: { opcao } })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível registrar o voto.'))
      return resposta.json()
    },
    onMutate: async ({ messageId, opcao, meuNome }) => {
      if (!channelId) return { anterior: undefined }
      const chave = chaveMensagens(channelId)
      // Uma busca em andamento sobrescreveria o voto recém-marcado.
      await queryClient.cancelQueries({ queryKey: chave })
      const anterior = queryClient.getQueryData<MensagemDoCanal[]>(chave)
      queryClient.setQueryData<MensagemDoCanal[]>(chave, (lista) =>
        lista?.map((m) =>
          m.id !== messageId
            ? m
            : {
                ...m,
                anexos: m.anexos.map((a) =>
                  a.tipo === 'enquete' && a.enquete ? { ...a, enquete: aplicarMeuVoto(a.enquete, opcao, meuNome) } : a,
                ),
              },
        ),
      )
      return { anterior }
    },
    onError: (_erro, _entrada, contexto) => {
      if (channelId && contexto?.anterior) queryClient.setQueryData(chaveMensagens(channelId), contexto.anterior)
    },
    // Confirma com a apuração do servidor (votos de outras pessoas no meio).
    onSettled: () => {
      if (channelId) void queryClient.invalidateQueries({ queryKey: chaveMensagens(channelId) })
    },
  })

  const apagar = useMutation({
    mutationFn: async (messageId: string) =>
      (await api.chat.messages[':messageId'].$delete({ param: { messageId } })).json(),
    onSuccess: invalidar,
  })

  const fixar = useMutation({
    mutationFn: async (messageId: string) =>
      (await api.chat.messages[':messageId'].pin.$post({ param: { messageId } })).json(),
    onSuccess: invalidar,
  })

  const favoritar = useMutation({
    mutationFn: async (messageId: string) =>
      (await api.chat.messages[':messageId'].favorite.$post({ param: { messageId } })).json(),
    onSuccess: invalidar,
  })

  const encaminhar = useMutation({
    mutationFn: async ({ paraCanal, messageId }: { paraCanal: string; messageId: string }) =>
      (
        await api.chat.channels[':id'].messages.$post({
          param: { id: paraCanal },
          json: { forwardFromMessageId: messageId },
        })
      ).json(),
    onSuccess: invalidar,
  })

  const abrirPrivado = useMutation({
    mutationFn: async (memberId: string) =>
      (await api.chat.channels.direct.$post({ json: { memberId } })).json(),
    onSuccess: invalidar,
  })

  const criarCanal = useMutation({
    mutationFn: async (entrada: { name: string; kind: string; description?: string | null; memberIds?: string[] }) =>
      (await api.chat.channels.$post({ json: entrada as never })).json(),
    onSuccess: invalidar,
  })

  const alterarCanal = useMutation({
    mutationFn: async (entrada: { name?: string; description?: string | null }) =>
      (await api.chat.channels[':id'].$patch({ param: { id: channelId! }, json: entrada })).json(),
    onSuccess: invalidar,
  })

  const adicionarMembros = useMutation({
    mutationFn: async (memberIds: string[]) =>
      (
        await api.chat.channels[':id'].members.$post({ param: { id: channelId! }, json: { memberIds } })
      ).json(),
    onSuccess: invalidar,
  })

  const removerMembro = useMutation({
    mutationFn: async (memberId: string) =>
      (
        await api.chat.channels[':id'].members[':memberId'].$delete({
          param: { id: channelId!, memberId },
        })
      ).json(),
    onSuccess: invalidar,
  })

  // Só a lista precisa saber (some o contador); a conversa aberta já está certa.
  const marcarLida = useMutation({
    mutationFn: async ({ canal, messageId }: { canal: string; messageId: string }) =>
      (await api.chat.channels[':id'].read.$post({ param: { id: canal }, json: { messageId } })).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_CANAIS }),
  })

  const trocarFoto = useMutation({
    mutationFn: async (arquivo: File) => {
      const form = new FormData()
      form.append('file', arquivo)
      const resposta = await fetch(`${apiOrigin()}/api/chat/channels/${channelId}/photo`, {
        method: 'POST',
        headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
        body: form,
      })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível trocar a foto.'))
    },
    onSuccess: invalidar,
  })

  const removerFoto = useMutation({
    mutationFn: async () => (await api.chat.channels[':id'].photo.$delete({ param: { id: channelId! } })).json(),
    onSuccess: invalidar,
  })

  return {
    trocarFoto,
    removerFoto,
    marcarLida,
    enviar,
    anexarEstruturado,
    reagir,
    votar,
    silenciar,
    apagar,
    fixar,
    favoritar,
    encaminhar,
    abrirPrivado,
    criarCanal,
    alterarCanal,
    adicionarMembros,
    removerMembro,
  }
}

/**
 * Ações do menu do botão direito na LISTA (pedido 2026-09-14: fixar,
 * favoritar, arquivar, marcar como não lida, limpar histórico, sair do
 * grupo). Separado de `useAcoesDoChat` de propósito: aqui a conversa-alvo é
 * um PARÂMETRO de cada chamada, não presa à conversa aberta — o menu age
 * sobre qualquer linha da lista, aberta ou não.
 */
export function useAcoesDaLista() {
  const queryClient = useQueryClient()
  const invalidar = () => void queryClient.invalidateQueries({ queryKey: CHAVE_CANAIS })

  const fixar = useMutation({
    mutationFn: async ({ channelId, fixar }: { channelId: string; fixar: boolean }) => {
      const resposta = await api.chat.channels[':id'].pin.$put({ param: { id: channelId }, json: { fixar } })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível fixar a conversa.'))
      return resposta.json()
    },
    onSuccess: invalidar,
  })

  /** Mesma rota de `useAcoesDoChat.silenciar`, mas com a conversa como PARÂMETRO. */
  const silenciar = useMutation({
    mutationFn: async ({ channelId, duracao }: { channelId: string; duracao: DuracaoDoSilencio | null }) => {
      const resposta = await api.chat.channels[':id'].mute.$put({ param: { id: channelId }, json: { duracao } })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível alterar as notificações.'))
      return resposta.json()
    },
    onSuccess: invalidar,
  })

  const favoritar = useMutation({
    mutationFn: async ({ channelId, favoritar }: { channelId: string; favoritar: boolean }) => {
      const resposta = await api.chat.channels[':id'].favorite.$put({ param: { id: channelId }, json: { favoritar } })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível favoritar a conversa.'))
      return resposta.json()
    },
    onSuccess: invalidar,
  })

  const arquivar = useMutation({
    mutationFn: async ({ channelId, arquivar }: { channelId: string; arquivar: boolean }) => {
      const resposta = await api.chat.channels[':id'].archive.$put({ param: { id: channelId }, json: { arquivar } })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível arquivar a conversa.'))
      return resposta.json()
    },
    onSuccess: invalidar,
  })

  const marcarNaoLida = useMutation({
    mutationFn: async (channelId: string) => {
      const resposta = await api.chat.channels[':id']['mark-unread'].$post({ param: { id: channelId } })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível marcar como não lida.'))
      return resposta.json()
    },
    onSuccess: invalidar,
  })

  const limpar = useMutation({
    mutationFn: async (channelId: string) => {
      const resposta = await api.chat.channels[':id'].clear.$post({ param: { id: channelId } })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível limpar a conversa.'))
      return resposta.json()
    },
    onSuccess: (_dados, channelId) => {
      invalidar()
      void queryClient.invalidateQueries({ queryKey: chaveMensagens(channelId) })
    },
  })

  const sairDoGrupo = useMutation({
    mutationFn: async ({ channelId, meuMemberId }: { channelId: string; meuMemberId: string }) => {
      const resposta = await api.chat.channels[':id'].members[':memberId'].$delete({
        param: { id: channelId, memberId: meuMemberId },
      })
      if (!resposta.ok) throw new Error(await mensagemDeErro(resposta, 'Não foi possível sair do grupo.'))
      return resposta.json()
    },
    onSuccess: invalidar,
  })

  return { fixar, silenciar, favoritar, arquivar, marcarNaoLida, limpar, sairDoGrupo }
}

/**
 * Mídia, documentos e links. `null` = de todas as minhas conversas.
 * Debaixo de `['chat', ...]`: mensagem nova ou apagada invalida junto.
 */
export function useAcervo(channelId: string | null, ligado = true) {
  return useQuery({
    queryKey: ['chat', 'channels', 'acervo', channelId ?? 'todas'],
    enabled: ligado,
    queryFn: async (): Promise<AcervoDaConversa> =>
      (await api.chat.library.$get({ query: channelId ? { channelId } : {} })).json(),
  })
}

/** Mensagens fixadas do canal. */
export function useFixadas(channelId: string | null) {
  return useQuery({
    queryKey: ['chat', 'pins', channelId],
    enabled: channelId !== null,
    queryFn: async () =>
      (await api.chat.channels[':id'].pins.$get({ param: { id: channelId! } })).json().then((r) => r.pins),
  })
}

/** Minhas favoritas, de todos os canais. */
export function useFavoritas() {
  return useQuery({
    queryKey: ['chat', 'favorites'],
    queryFn: async () => (await api.chat.favorites.$get()).json().then((r) => r.favorites),
  })
}

/**
 * Selo do ícone do chat: total de não lidas de todas as conversas.
 *
 * Vive no shell, em qualquer tela — por isso ouve o canal ao vivo sozinho (a
 * `useChatAoVivo` só existe com a Comunicação aberta). A chave fica DEBAIXO de
 * `CHAVE_CANAIS`: tudo que invalida a lista (ler, enviar, apagar) zera ou
 * recalcula o selo junto, sem ninguém precisar lembrar dele.
 */
export function useNaoLidasDoChat(ligado: boolean) {
  const queryClient = useQueryClient()
  /**
   * A PRIMEIRA `abertura` é a conexão inicial, que chega segundos depois do
   * carregamento — quando a consulta abaixo já rodou. Invalidar ali pedia a
   * mesma coisa duas vezes a cada carregamento (SPEC-20260917-1836). Só as
   * aberturas seguintes são reconexão de verdade, que é o caso que importa:
   * mensagens podem ter chegado com o canal caído, sem ninguém avisar.
   */
  const primeiraAbertura = useRef(true)
  useEffect(() => {
    if (!ligado) return
    return canalDePresenca.inscrever((evento) => {
      if (evento.tipo === 'abertura') {
        if (primeiraAbertura.current) {
          primeiraAbertura.current = false
          return
        }
        void queryClient.invalidateQueries({ queryKey: [...CHAVE_CANAIS, 'nao-lidas'] })
        return
      }
      if (evento.tipo === 'chat' || evento.tipo === 'mencao') {
        void queryClient.invalidateQueries({ queryKey: [...CHAVE_CANAIS, 'nao-lidas'] })
      }
    })
  }, [ligado, queryClient])

  return useQuery({
    queryKey: [...CHAVE_CANAIS, 'nao-lidas'],
    enabled: ligado,
    queryFn: async (): Promise<{ naoLidas: number; mencoes: number }> => (await api.chat.unread.$get()).json(),
  })
}

/** Preset de "Meus Estilos" (SPEC-20260917-0010). */
export type EstiloDoChat = {
  id: string
  name: string
  backgroundColor: string | null
  wallpaper: string | null
  myBubbleColor: string | null
  otherBubbleColor: string | null
  isActive: boolean
}

const CHAVE_ESTILOS = ['chat', 'style-presets']

/**
 * Presets de aparência da Comunicação, salvos por nome, e o que está ATIVO
 * agora (no máximo um). Usado tanto pela tela "Meus Estilos" (CRUD completo)
 * quanto pelo menu rápido de personalizar (edita o preset ativo).
 */
export function useEstilosDoChat() {
  const queryClient = useQueryClient()
  const presets = useQuery({
    queryKey: CHAVE_ESTILOS,
    queryFn: async (): Promise<EstiloDoChat[]> =>
      (await api.chat['style-presets'].$get()).json().then((r) => r.presets),
  })
  const invalidar = () => queryClient.invalidateQueries({ queryKey: CHAVE_ESTILOS })

  const criar = useMutation({
    mutationFn: async (input: Omit<EstiloDoChat, 'id' | 'isActive'>) =>
      (await api.chat['style-presets'].$post({ json: input })).json(),
    onSuccess: invalidar,
  })
  const atualizar = useMutation({
    mutationFn: async ({ id, ...input }: Partial<Omit<EstiloDoChat, 'id' | 'isActive'>> & { id: string }) =>
      (await api.chat['style-presets'][':id'].$patch({ param: { id }, json: input })).json(),
    onSuccess: invalidar,
  })
  const excluir = useMutation({
    mutationFn: async (id: string) => api.chat['style-presets'][':id'].$delete({ param: { id } }),
    onSuccess: invalidar,
  })
  const aplicar = useMutation({
    mutationFn: async (id: string) => api.chat['style-presets'][':id'].apply.$post({ param: { id } }),
    onSuccess: invalidar,
  })

  return { presets, ativo: presets.data?.find((p) => p.isActive) ?? null, criar, atualizar, excluir, aplicar }
}

/** Mensagens que me mencionaram, de todas as conversas — o filtro Menções. */
export function useMencoes(ligado: boolean) {
  return useQuery({
    // Debaixo de `CHAVE_CANAIS`: o canal ao vivo e a leitura já invalidam.
    queryKey: [...CHAVE_CANAIS, 'mencoes'],
    enabled: ligado,
    queryFn: async (): Promise<MencaoRecebida[]> =>
      (await api.chat.mentions.$get()).json().then((r) => r.mencoes),
  })
}

/** Mensagens de todas as minhas conversas que contêm o termo (caixa de pesquisa). */
export function useBuscaDeMensagens(termo: string) {
  const busca = useDeferredValue(termo.trim())
  return useQuery({
    queryKey: ['chat', 'search', busca],
    enabled: busca.length >= 2,
    queryFn: async (): Promise<ResultadoDeBusca[]> =>
      (await api.chat.search.$get({ query: { q: busca } })).json().then((r) => r.resultados),
  })
}

/** URL da foto da linha: a do grupo ou a da outra pessoa (conversa direta). */
export function urlDaFoto(foto: FotoDoCanal | null): string | null {
  if (!foto) return null
  if (foto.tipo === 'grupo') {
    return `${apiOrigin()}/public/channel-photo/${foto.channelId}?v=${encodeURIComponent(foto.versao ?? '')}`
  }
  return avatarDoAutor({
    memberId: foto.memberId,
    avatarUrl: foto.avatarUrl,
    hasAvatar: foto.temAvatar,
    avatarVersion: foto.versao,
  })
}

/** GIFs do Giphy pelo servidor: em alta sem termo, busca com termo. */
export function useGifs(termo: string) {
  const busca = useDeferredValue(termo.trim())
  return useQuery({
    queryKey: ['chat', 'gifs', busca],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ configurado: boolean; gifs: GifDoGiphy[] }> =>
      (await api.chat.gifs.$get({ query: { q: busca } })).json(),
  })
}

/** A mensagem do `{ error }` da API, ou o texto padrão. */
async function mensagemDeErro(resposta: Response, padrao: string): Promise<string> {
  try {
    const dados = (await resposta.json()) as { error?: { message?: string } }
    return dados.error?.message ?? padrao
  } catch {
    return padrao
  }
}

/** URL do anexo. Rota pública: `<img>` e `<a download>` não mandam header. */
export const urlDoAnexo = (attachmentId: string) => `${apiOrigin()}/public/chat-attachment/${attachmentId}`

/** Banner do perfil de alguém (2026-09-16). Null quando a pessoa não pôs um. */
export function urlDoBanner(membro: { memberId: string; hasBanner: boolean; avatarVersion: string | null }): string | null {
  if (!membro.hasBanner) return null
  return `${apiOrigin()}/public/member-banner/${membro.memberId}?v=${encodeURIComponent(membro.avatarVersion ?? '')}`
}

/** Foto de quem enviou — mesma regra do resto do app. */
export function avatarDoAutor(autor: {
  memberId: string
  avatarUrl: string | null
  hasAvatar: boolean
  avatarVersion: string | null
}): string | null {
  if (autor.hasAvatar) {
    return `${apiOrigin()}/public/member-avatar/${autor.memberId}?v=${encodeURIComponent(autor.avatarVersion ?? '')}`
  }
  return autor.avatarUrl
}

export type { StatusEscolhido }

/* ------------------------------------------------------ digitando --- */

/**
 * Quem está digitando na conversa aberta.
 *
 * Efêmero, fora do React Query: cada aviso renova um prazo e o nome some
 * sozinho quando o prazo vence — não há quadro de "parou de digitar". Uma
 * mensagem nova da pessoa também apaga, senão o aviso sobraria segundos depois
 * de a frase já estar na tela.
 */
export function useDigitando(channelId: string | null): string[] {
  const [quem, setQuem] = useState<Map<string, { nome: string; ate: number }>>(new Map())

  useEffect(() => {
    setQuem(new Map())
    if (!channelId) return
    const desinscrever = canalDePresenca.inscrever((evento) => {
      if (evento.tipo === 'digitando' && evento.channelId === channelId) {
        setQuem((atual) => new Map(atual).set(evento.memberId, { nome: evento.nome, ate: Date.now() + DIGITANDO_EXPIRA_MS }))
      }
      if (evento.tipo === 'chat' && evento.channelId === channelId && evento.acao === 'nova') {
        // Não se sabe o autor pelo aviso; limpar todos custa no máximo um
        // piscar, e evita "digitando…" embaixo da mensagem já chegada.
        setQuem(new Map())
      }
    })
    const relogio = setInterval(() => {
      setQuem((atual) => {
        const agora = Date.now()
        const vivos = [...atual].filter(([, v]) => v.ate > agora)
        return vivos.length === atual.size ? atual : new Map(vivos)
      })
    }, 1_000)
    return () => {
      desinscrever()
      clearInterval(relogio)
    }
  }, [channelId])

  return useMemo(() => [...quem.values()].map((v) => v.nome), [quem])
}

/** Avisa que estou digitando, no máximo a cada `DIGITANDO_REENVIO_MS`. */
export function useAvisarDigitando(channelId: string | null): () => void {
  const ultimo = useRef(0)
  return useCallback(() => {
    if (!channelId) return
    const agora = Date.now()
    if (agora - ultimo.current < DIGITANDO_REENVIO_MS) return
    ultimo.current = agora
    canalDePresenca.avisarDigitando(channelId)
  }, [channelId])
}

/* -------------------------------------- contatos de outras empresas --- */

export const CHAVE_CONTATOS = ['chat', 'contacts'] as const

/**
 * Meus contatos de fora e os convites pendentes.
 *
 * Só roda com o toggle CHAT_EXTERNAL_CONTACTS ligado — desligado, a API
 * responde 403 e a tela nem mostra a seção.
 */
export function useContatos(ligado: boolean) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!ligado) return
    return canalDePresenca.inscrever((evento) => {
      if (evento.tipo !== 'contato') return
      void queryClient.invalidateQueries({ queryKey: CHAVE_CONTATOS })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'people'] })
      // Contato aceito pode ter aberto conversa nova do outro lado.
      void queryClient.invalidateQueries({ queryKey: CHAVE_CANAIS })
    })
  }, [ligado, queryClient])

  return useQuery({
    queryKey: CHAVE_CONTATOS,
    enabled: ligado,
    queryFn: async (): Promise<{ contatos: PessoaDoOrbind[]; convites: ConviteDeContato[] }> =>
      (await api.chat.contacts.$get()).json(),
  })
}

/** Busca em todo o Orbind, fora do workspace. */
export function useBuscaDePessoas(ligado: boolean, termo: string) {
  const busca = useDeferredValue(termo.trim())
  return useQuery({
    queryKey: ['chat', 'people', busca],
    enabled: ligado && busca.length >= BUSCA_MINIMA_DE_PESSOAS,
    queryFn: async (): Promise<PessoaDoOrbind[]> =>
      (await api.chat.contacts.search.$get({ query: { q: busca } })).json().then((r) => r.pessoas),
  })
}

export function useAcoesDeContato() {
  const queryClient = useQueryClient()
  const invalidar = () => {
    void queryClient.invalidateQueries({ queryKey: CHAVE_CONTATOS })
    void queryClient.invalidateQueries({ queryKey: ['chat', 'people'] })
  }
  return {
    convidar: useMutation({
      mutationFn: async (memberId: string) =>
        (await api.chat.contacts.invites.$post({ json: { memberId } })).json(),
      onSuccess: invalidar,
    }),
    aceitar: useMutation({
      mutationFn: async (id: string) =>
        (await api.chat.contacts.invites[':id'].accept.$post({ param: { id } })).json(),
      onSuccess: invalidar,
    }),
    recusar: useMutation({
      mutationFn: async (id: string) =>
        (await api.chat.contacts.invites[':id'].decline.$post({ param: { id } })).json(),
      onSuccess: invalidar,
    }),
    cancelar: useMutation({
      mutationFn: async (id: string) =>
        (await api.chat.contacts.invites[':id'].$delete({ param: { id } })).json(),
      onSuccess: invalidar,
    }),
  }
}
